import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import PropTypes from "prop-types";
import { VIRTUAL_BACKGROUND } from "../../constants/VirtualBackgroundConstant";
import { MEDIA_PERMISSIONS } from "../../constants/CallSettingConstants";
import VirtualBackgroundUploadImage from "./VirtualBackgroundUploadImage";
import VirtualBackgroundDefaultImages from "./VirtualBackgroundDefaultImages";
import { applyVirtualBackgroundEffect } from "./VirtualBackgroundUtils.js";
import { setSelectedVirtualBackgroundImageId, setVirtualBackgroundLocalImageUrl } from "../../reducers/call-setting-reducer.js";
import useLanguageChange from "../../hooks/useLanguageChange.js";
import storage from "../../services/storage.js";
import { LOCAL_STORAGE_KEYS } from "../../constants/Constants.js";
import { LogFirebase } from "../../services/firestore.js";

const VirtualBackground = ({ cameraPermission, cameraErrorMsg }) => {
    const dispatch = useDispatch();

    const { selectedVirtualBackgroundImageId, virtualBackgroundLocalImageUrl, startCallSettingLocalVideo } = useSelector(
        (state) => state.callSetting
    );
    const { isLocalVideoAttached } = useSelector((state) => state.twilioEvents);

    const localisedValues = useLanguageChange();

    useEffect(() => {
        if (startCallSettingLocalVideo || isLocalVideoAttached) {
            const localUploadedImageUrl =
                selectedVirtualBackgroundImageId === VIRTUAL_BACKGROUND.LOCAL_IMAGE_ID ? virtualBackgroundLocalImageUrl : null;
            applyVirtualBackgroundEffect(selectedVirtualBackgroundImageId, localUploadedImageUrl);
            LogFirebase("VirtualBackground", "apply_from_callsetting_with_no_video_on", {
                selectedVirtualBackgroundImageId: selectedVirtualBackgroundImageId,
            });
        }
    }, [startCallSettingLocalVideo, selectedVirtualBackgroundImageId, virtualBackgroundLocalImageUrl, isLocalVideoAttached]);

    const handleButtonClick = async (bg, imageUrl = "") => {
        dispatch(setSelectedVirtualBackgroundImageId(bg));
        storage.set(LOCAL_STORAGE_KEYS.SELECTED_VIRTUAL_BACKGROUND_IMAGE_ID, bg); // set the selected image id in local storage
        if (bg === VIRTUAL_BACKGROUND.LOCAL_IMAGE_ID && imageUrl) {
            dispatch(setVirtualBackgroundLocalImageUrl(imageUrl));
        }
        LogFirebase("VirtualBackground", "handleButtonClick", { background: bg });
    };

    return (
        <div className='tab-pane call-settings-tab-pane fade show' id='virtual-background' role='tabpanel'>
            <div className='virtual-background call-settings-scroll custom-scroll'>
                <div className='call-settings-video'>
                    <div className='call-settings-video-box'>
                        {cameraPermission !== MEDIA_PERMISSIONS.BLOCKED && cameraPermission !== MEDIA_PERMISSIONS.ERROR && (
                            <div className='call-settings-video-loader' id='showCamLoading_vb'>
                                <div className='round-loader default-loader'></div>
                            </div>
                        )}
                        {cameraPermission !== MEDIA_PERMISSIONS.BLOCKED && cameraPermission !== MEDIA_PERMISSIONS.ERROR && (
                            <div className='video' id='callsettingVideo_vb'></div>
                        )}
                        {cameraPermission === MEDIA_PERMISSIONS.GRANTED && <div className='call-settings-video-2' id='localVideoContainer_vb'></div>}
                        {(cameraPermission === MEDIA_PERMISSIONS.BLOCKED || cameraPermission === MEDIA_PERMISSIONS.ERROR) && (
                            <div className='call-settings-video-info'>
                                <div className='block-icon'>
                                    <span className='for-icon icon-video-off'></span>
                                </div>
                                {cameraErrorMsg}
                            </div>
                        )}
                    </div>
                </div>
                <div className='vbg-effect-selection d-flex'>
                    <div className='button'>
                        <div className='button-in custom-tooltip tooltip-auto-width center-tooltip m-hide margin-0'>
                            <button
                                className={`btn vbg-effect-btn ${selectedVirtualBackgroundImageId === VIRTUAL_BACKGROUND.NONE_EFFECT_ID && "active"}`}
                                disabled={selectedVirtualBackgroundImageId === VIRTUAL_BACKGROUND.NONE_EFFECT_ID}
                                onClick={() => handleButtonClick(VIRTUAL_BACKGROUND.NONE_EFFECT_ID)}
                            >
                                <span className='btn-icon icon-effect-none'></span>
                            </button>
                            <div className='custom-tooltip-content text-nowrap'>
                                <div className='custom-tooltip-in'>
                                    <p>{localisedValues?.no_effect}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='button'>
                        <div className='button-in custom-tooltip tooltip-auto-width center-tooltip m-hide margin-0'>
                            <button
                                className={`btn vbg-effect-btn ${selectedVirtualBackgroundImageId === VIRTUAL_BACKGROUND.BLUR_EFFECT_ID && "active"}`}
                                disabled={selectedVirtualBackgroundImageId === VIRTUAL_BACKGROUND.BLUR_EFFECT_ID}
                                onClick={() => handleButtonClick(VIRTUAL_BACKGROUND.BLUR_EFFECT_ID)}
                            >
                                <span className='btn-icon icon-blur-image'></span>
                            </button>
                            <div className='custom-tooltip-content text-nowrap'>
                                <div className='custom-tooltip-in'>
                                    <p>{localisedValues?.blur_background}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <VirtualBackgroundUploadImage localisedValues={localisedValues} />
                <VirtualBackgroundDefaultImages handleButtonClick={handleButtonClick} localisedValues={localisedValues} />
            </div>
        </div>
    );
};

VirtualBackground.propTypes = {
    cameraPermission: PropTypes.string,
    cameraErrorMsg: PropTypes.any,
};

export default VirtualBackground;
